import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ReferralCodeCard } from './components/ReferralCodeCard';
import { ReferralCodeModal } from './components/ReferralCodeModal';
import { UsersModal } from './components/UsersModal';
import { referralCodesService, type ReferralCodeData } from './services/referralCodesService';

export const ReferralCodes = () => {
  const [codes, setCodes] = useState<ReferralCodeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<ReferralCodeData | undefined>(undefined);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [selectedCodeForUsers, setSelectedCodeForUsers] = useState<ReferralCodeData | undefined>(undefined);

  const loadReferralCodes = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await referralCodesService.getAllReferralCodes();
      setCodes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referral codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferralCodes();
  }, []);

  const handleCreateNew = () => {
    setSelectedCode(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (code: ReferralCodeData) => {
    setSelectedCode(code);
    setIsModalOpen(true);
  };

  const handleSuccess = (updatedCode: ReferralCodeData, isEdit: boolean) => {
    if (isEdit) {
      // Update existing code in list
      setCodes((prev) => prev.map((c) => (c.code === updatedCode.code ? updatedCode : c)));
    } else {
      // Add new code to top of list
      setCodes((prev) => [updatedCode, ...prev]);
    }
    setIsModalOpen(false);
    setSelectedCode(undefined);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCode(undefined);
  };

  const handleDelete = async (codeToDelete: string) => {
    try {
      await referralCodesService.deleteReferralCode(codeToDelete);
      setCodes((prev) => prev.filter((c) => c.code !== codeToDelete));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete referral code');
    }
  };

  const handleViewUsers = (code: string) => {
    const codeData = codes.find((c) => c.code === code);
    if (codeData) {
      setSelectedCodeForUsers(codeData);
      setIsUsersModalOpen(true);
    }
  };

  const handleCloseUsersModal = () => {
    setIsUsersModalOpen(false);
    setSelectedCodeForUsers(undefined);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorDisplay message={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Referral Codes</h1>
              <p className="text-gray-600 mt-2">
                View and manage referral codes and their performance
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Total codes: {codes.length}
              </p>
            </div>
            <Button
              onClick={handleCreateNew}
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
            >
              Create New Code
            </Button>
          </div>
        </div>

        {/* Referral Codes Grid */}
        {codes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">No referral codes found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {codes.map((code) => (
              <ReferralCodeCard 
                key={code.code} 
                code={code} 
                onEdit={handleEdit} 
                onDelete={handleDelete}
                onViewUsers={handleViewUsers}
              />
            ))}
          </div>
        )}

        {/* Referral Code Modal */}
        <ReferralCodeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          code={selectedCode}
          onSuccess={handleSuccess}
        />

        {/* Users Modal */}
        <UsersModal
          isOpen={isUsersModalOpen}
          onClose={handleCloseUsersModal}
          code={selectedCodeForUsers?.code || ''}
          codeName={selectedCodeForUsers?.code || ''}
        />
      </div>
    </div>
  );
};

export default ReferralCodes;
